/**
 * PCM Audio Manager
 * Captures audio from microphone and converts to Int16 PCM for streaming.
 *
 * Uses AudioWorklet (modern, reliable) with ScriptProcessorNode fallback (deprecated).
 * Includes health monitoring to detect and recover from silent pipeline stalls.
 */

import { Sentry } from '@/lib/sentry';
import {
  PREROLL_MS,
  SILENCE_RMS_THRESHOLD,
  computeFrameRms,
  computeInt16Rms,
  shouldTransmit,
} from '@/lib/silenceDetection';
import { requestMicrophone } from '@careflux/utils/microphone';

// ── Public types ──────────────────────────────────────────────────────────────

export type AudioRecoveryResult = 'recovered' | 'in-progress' | 'stopped';

export interface PCMChunk {
  pcmData: Int16Array; // For metadata/playback
  pcmArrayBuffer: ArrayBuffer; // For WebSocket transmission
  incrementalDuration: number; // Duration of this chunk only
  totalDuration: number; // Cumulative duration
  totalSamples: number; // Total samples sent so far
  sampleRate: number;
  channels: number;
  bitDepth: number;
  /**
   * Cumulative CAPTURED samples at the end of this chunk's audio, counted at
   * `sampleRate`. The payload occupies capture range
   * `[captureEndSample - pcmData.length, captureEndSample)`.
   *
   * Distinct from `totalSamples`, which counts only TRANSMITTED samples — the
   * silence gate below drops frames from the stream but never from the capture
   * counter or from `allPCMData` (the audio the recording file is built from).
   * Capture offsets therefore index that file exactly, which is what lets the
   * server stamp segment times in the same space the file is addressed in.
   */
  captureEndSample: number;
  /** Identity of the capture epoch these offsets belong to (one per manager). */
  captureEpochId: string;
}

/** A single synchronous snapshot of the retained PCM and its capture offsets. */
export interface CaptureUploadSnapshot {
  pcm: Int16Array;
  captureStartSample: number;
  captureEndSample: number;
  captureEpochId: string;
  sampleRate: number;
}

export interface PCMAudioManagerConfig {
  sampleRate?: number;
  channels?: number;
  bitDepth?: number;
  transmitInterval?: number; // How often to send chunks (ms)
  /** Call audio is transit-only; normal recordings retain their existing behaviour. */
  retainAudio?: boolean;
  /** A supplied call track must never recover by opening the local microphone. */
  requireExistingStream?: boolean;
  /** Keep the two call timelines aligned, including silence. */
  transmitSilence?: boolean;
}

export type AudioManagerState = 'idle' | 'recording' | 'pausing' | 'paused' | 'recovering' | 'stopped';

/** `heardSound` is false while no frame since start() has crossed the RMS threshold (dead or muted mic). */
export type SilenceStateCallback = (isSilent: boolean, silenceDurationMs: number, heardSound?: boolean) => void;

export interface AudioPipelineHealth {
  isHealthy: boolean;
  lastFrameAgeMs: number;
  audioContextState: AudioContextState | null;
  mediaTrackState: MediaStreamTrackState | null;
  mediaTrackMuted: boolean;
  frameCount: number;
  totalDurationSec: number;
  isPaused: boolean;
  usingWorklet: boolean;
  tabVisible: boolean;
  pauseResumeCount: number;
  heapUsedMB: number | null; // Chrome-only
  audioDeviceId: string | null; // NOT label (could contain user name)
  consecutiveStallChecks: number;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const DEFAULT_CONFIG = {
  sampleRate: 16000,
  channels: 1,
  bitDepth: 16,
  transmitInterval: 500, // 500ms for faster VAD response (was 1500)
  retainAudio: true,
  requireExistingStream: false,
  transmitSilence: false,
};

const HEALTH_CHECK_INTERVAL_MS = 5_000;
const STALL_THRESHOLD_MS = 5_000;
const CONSECUTIVE_STALLS_REQUIRED = 2;
const VISIBILITY_GRACE_PERIOD_MS = 3_000;
const SENTRY_BREADCRUMB_INTERVAL_MS = 30_000;

// ── Class ─────────────────────────────────────────────────────────────────────

export class PCMAudioManager {
  private config: Required<PCMAudioManagerConfig>;

  // Audio graph nodes
  private audioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private processorNode: AudioWorkletNode | ScriptProcessorNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private silentGain: GainNode | null = null;

  // Buffers & counters
  private pendingBuffers: Int16Array[] = [];
  private totalDuration = 0;
  private totalSamples = 0;
  private transmitTimer: ReturnType<typeof setInterval> | null = null;
  private frameCount = 0;

  // Store all PCM data for debugging/playback
  private allPCMData: Int16Array[] = [];

  // Capture clock: every captured sample counted, including silence the
  // transmit gate drops. Invariant:
  //   capturedSamples === (samples in allPCMData) + (samples cleared by clearPCMData())
  private capturedSamples = 0;

  // One epoch per manager instance: survives pause()/resume() and recover(),
  // dies with the manager. A new manager (new recording, page reload) mints a
  // new epoch, which the server anchors at its own offset in the audio file.
  readonly captureEpochId: string = crypto.randomUUID();

  // Callback
  private onPCMDataCallback?: (chunk: PCMChunk) => void;
  private canRetryRecovery = false;

  // State machine
  private state: AudioManagerState = 'idle';
  private pauseResumeCount = 0;

  // Health monitoring
  private lastAudioFrameTime = 0;
  private healthCheckTimer: ReturnType<typeof setInterval> | null = null;
  private breadcrumbTimer: ReturnType<typeof setInterval> | null = null;
  private stallReportedToSentry = false;
  private consecutiveStallChecks = 0;
  private usingWorklet = false;
  private lastDeviceId?: string;

  // Visibility handling
  private visibilityHandler: (() => void) | null = null;
  private visibilityGraceTimer: ReturnType<typeof setTimeout> | null = null;
  private healthCheckPausedForVisibility = false;

  // Callbacks
  private onStallCallback?: (health: AudioPipelineHealth) => void;

  // Silence detection / transmit gating
  private lastSoundTime = 0;
  private recordingStartedAt = 0;
  private hasHeardSound = false;
  private prerollBuffers: Int16Array[] = [];
  private prerollSamplesMax = Math.round((PREROLL_MS / 1000) * DEFAULT_CONFIG.sampleRate);
  private prerollSamples = 0;
  private onSilenceStateChangeCallback?: SilenceStateCallback;
  private __nowForTest?: number;

  constructor(config: PCMAudioManagerConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    // Recompute after config is resolved (in case a custom sampleRate was passed)
    this.prerollSamplesMax = Math.round((PREROLL_MS / 1000) * this.config.sampleRate);
  }

  // ── Public API ────────────────────────────────────────────────────────────

  /**
   * Start capturing audio from microphone.
   * Can optionally accept an existing MediaStream to share with Web Speech API.
   */
  async start(deviceId?: string, onPCMData?: (chunk: PCMChunk) => void, existingStream?: MediaStream): Promise<void> {
    this.canRetryRecovery = false;
    this.onPCMDataCallback = onPCMData;
    this.lastDeviceId = deviceId;

    if (
      this.config.requireExistingStream &&
      !existingStream?.getAudioTracks().some((track) => track.readyState === 'live')
    ) {
      throw new Error('The supplied call audio track is unavailable');
    }

    // Use existing stream if provided, otherwise request microphone access
    if (existingStream) {
      this.mediaStream = existingStream;
    } else {
      this.mediaStream = await requestMicrophone({
        audio: {
          deviceId: deviceId ? { exact: deviceId } : undefined,
          channelCount: this.config.channels,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    }

    // Listen for track ending (browser revokes mic, Bluetooth disconnect, etc.)
    // Remove first to prevent duplicate listeners on recovery (reused stream)
    const track = this.mediaStream.getAudioTracks()[0];
    if (track) {
      track.removeEventListener('ended', this.handleTrackEnded);
      track.addEventListener('ended', this.handleTrackEnded);
    }

    // Capture at 16kHz — matches ASR input rate, avoids server-side resampling,
    // and reduces WebSocket bandwidth + memory by 2/3 vs native 48kHz.
    this.audioContext = new AudioContext({ sampleRate: 16000 });

    // Resume AudioContext if suspended (required in modern browsers after user gesture)
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    // Monitor AudioContext state changes
    this.audioContext.addEventListener('statechange', this.handleAudioContextStateChange);

    // Use the AudioContext's actual sample rate (which matches hardware)
    this.config.sampleRate = this.audioContext.sampleRate;

    // Create source from stream
    this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream);

    // Create processor node (AudioWorklet or ScriptProcessor fallback)
    await this.createProcessorNode();

    // Set state to 'recording' BEFORE connecting the processor graph
    // so that audio frames arriving immediately are not dropped by the
    // state !== 'recording' guard in the worklet onmessage handler
    this.state = 'recording';
    this.recordingStartedAt = Date.now();
    this.hasHeardSound = false;

    // Connect nodes: source → processor → silentGain → destination
    if (this.processorNode) this.sourceNode.connect(this.processorNode);
    this.silentGain = this.audioContext.createGain();
    // Use tiny non-zero value to prevent Chrome from throttling the audio graph
    this.silentGain.gain.value = 1e-10;
    this.processorNode?.connect(this.silentGain);
    this.silentGain.connect(this.audioContext.destination);

    // Start transmission timer
    this.startTransmitTimer();

    // Start health monitoring
    this.lastAudioFrameTime = Date.now();
    this.startHealthCheck();
    this.startBreadcrumbs();
    this.startVisibilityMonitoring();
  }

  /**
   * Stop capturing and send final data
   */
  async stop(): Promise<void> {
    this.canRetryRecovery = false;
    this.state = 'stopped';

    this.stopHealthCheck();
    this.stopBreadcrumbs();
    this.stopVisibilityMonitoring();

    // Stop timer
    if (this.transmitTimer) {
      clearInterval(this.transmitTimer);
      this.transmitTimer = null;
    }

    // Send final chunk
    this.transmitPCMData();

    await this.teardownAudioGraph(true);

    // Reset state
    this.pendingBuffers = [];
    this.totalDuration = 0;
    this.totalSamples = 0;
    this.frameCount = 0;
    this.allPCMData = [];
    this.capturedSamples = 0;
    this.pauseResumeCount = 0;
    this.consecutiveStallChecks = 0;
    this.stallReportedToSentry = false;
  }

  /**
   * Pause audio capture without destroying the audio context or media stream.
   */
  pause(): void {
    if (this.state !== 'recording') return;
    this.state = 'pausing';

    // Stop health check during pause (no audio frames expected)
    this.stopHealthCheck();

    // Stop transmit timer
    if (this.transmitTimer) {
      clearInterval(this.transmitTimer);
      this.transmitTimer = null;
    }

    // Send pending data before pause
    this.transmitPCMData();

    // Disconnect source from processor (stops audio capture)
    if (this.processorNode && this.sourceNode) {
      this.sourceNode.disconnect(this.processorNode);
    }

    this.state = 'paused';
    this.pauseResumeCount++;
  }

  /**
   * Resume audio capture after pause.
   */
  resume(): void {
    if (this.state !== 'paused') return;

    // Reconnect audio processing
    if (this.sourceNode && this.processorNode) {
      this.sourceNode.connect(this.processorNode);
    }

    // Restart transmit timer
    this.startTransmitTimer();

    // Restart health monitoring
    this.lastAudioFrameTime = Date.now();
    this.consecutiveStallChecks = 0;
    this.startHealthCheck();

    this.state = 'recording';
    this.pauseResumeCount++;
  }

  /**
   * Recover from a stalled audio pipeline without losing session state.
   * Reuses the existing MediaStream if tracks are still alive.
   * Preserves totalDuration/totalSamples for server-side continuity.
   */
  async recover(): Promise<AudioRecoveryResult> {
    if (this.state === 'recovering') return 'in-progress';
    if (this.state === 'stopped' && !this.canRetryRecovery) return 'stopped';

    this.state = 'recovering';

    // Save state to transfer to new pipeline
    const savedTotalDuration = this.totalDuration;
    const savedTotalSamples = this.totalSamples;
    const savedFrameCount = this.frameCount;
    const savedCallback = this.onPCMDataCallback;
    const savedDeviceId = this.lastDeviceId;
    const savedSampleRate = this.config.sampleRate;
    const savedHasHeardSound = this.hasHeardSound;

    // Check if MediaStream is still alive — reuse if possible to avoid permission popup
    const track = this.mediaStream?.getAudioTracks()[0];
    const trackAlive = track?.readyState === 'live';
    const existingStream = trackAlive ? this.mediaStream : undefined;

    // Tear down audio processing graph (NOT the stream if reusable)
    this.stopHealthCheck();
    this.stopBreadcrumbs();
    this.stopVisibilityMonitoring();

    if (this.transmitTimer) {
      clearInterval(this.transmitTimer);
      this.transmitTimer = null;
    }

    // Null the callback before teardown to prevent stale transmits
    this.onPCMDataCallback = undefined;

    await this.teardownAudioGraph(false);

    // If stream is dead, clean up track listener (start() will get a new stream)
    if (!trackAlive && this.mediaStream) {
      if (track) {
        track.removeEventListener('ended', this.handleTrackEnded);
      }
      this.mediaStream = null;
    }

    // Reset buffers but NOT counters
    this.pendingBuffers = [];

    // Rebuild processing graph — start() sets state to 'recording' internally
    try {
      await this.start(savedDeviceId, savedCallback, existingStream ?? undefined);
    } catch (err) {
      // Recovery failed (e.g., getUserMedia denied) — reset to stopped state
      // so future recover() calls aren't permanently blocked
      this.state = 'stopped';
      this.canRetryRecovery = true;
      console.error('[PCMAudioManager] Recovery failed:', err);
      throw err;
    }

    // A fresh AudioContext can come up at a different hardware rate than the
    // one we were capturing at (a Bluetooth headset swapped mid-recording is
    // the usual cause). Appending those samples to the same capture stream
    // would splice two rates into one recording file: its header keeps the old
    // rate, so the appended audio plays back at the wrong speed, and every
    // sample offset past the splice addresses the wrong moment. Refuse instead
    // — audio captured before the stall is intact and still uploadable, and
    // callers already handle a rejected recover() as a recording failure.
    if (this.config.sampleRate !== savedSampleRate) {
      Sentry.captureMessage('audio_recover_sample_rate_changed', {
        level: 'error',
        extra: { previousSampleRate: savedSampleRate, newSampleRate: this.config.sampleRate },
      });
      console.error(
        `[PCMAudioManager] Recovery aborted: sample rate changed ${savedSampleRate} -> ${this.config.sampleRate}`
      );

      this.stopHealthCheck();
      this.stopBreadcrumbs();
      this.stopVisibilityMonitoring();
      if (this.transmitTimer) {
        clearInterval(this.transmitTimer);
        this.transmitTimer = null;
      }
      this.onPCMDataCallback = undefined;
      this.pendingBuffers = [];
      await this.teardownAudioGraph(true);

      this.state = 'stopped';
      throw new Error('sample rate changed during recovery');
    }

    // Restore cumulative counters (critical — server relies on monotonic totalDuration)
    this.totalDuration = savedTotalDuration;
    this.totalSamples = savedTotalSamples;
    this.frameCount = savedFrameCount;
    this.hasHeardSound = savedHasHeardSound;

    console.info('[PCMAudioManager] Pipeline recovered successfully');
    return 'recovered';
  }

  /**
   * Register callback for pipeline stall detection.
   */
  onStall(callback: (health: AudioPipelineHealth) => void): void {
    this.onStallCallback = callback;
  }

  /** Register a callback fired when live-stream silence state changes / persists. */
  onSilenceStateChange(callback: SilenceStateCallback): void {
    this.onSilenceStateChangeCallback = callback;
  }

  /**
   * Reset silence-tracking state — clears lastSoundTime and the pre-roll buffer
   * and restarts the silence window from now.
   * Call this when starting a fresh silence "window", e.g., after auto-pause was
   * resumed by the user, so the next 5-minute countdown starts from the next
   * detected sound, not from before the pause.
   */
  resetSilenceTracking(): void {
    this.lastSoundTime = 0;
    this.recordingStartedAt = Date.now();
    this.prerollBuffers = [];
    this.prerollSamples = 0;
  }

  /**
   * Get current health snapshot.
   */
  getHealth(): AudioPipelineHealth {
    const track = this.mediaStream?.getAudioTracks()[0];
    const perfMemory = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;

    return {
      isHealthy: this.state === 'recording' && this.consecutiveStallChecks < CONSECUTIVE_STALLS_REQUIRED,
      lastFrameAgeMs: this.lastAudioFrameTime ? Date.now() - this.lastAudioFrameTime : -1,
      audioContextState: this.audioContext?.state ?? null,
      mediaTrackState: track?.readyState ?? null,
      mediaTrackMuted: track?.muted ?? false,
      frameCount: this.frameCount,
      totalDurationSec: this.totalDuration,
      isPaused: this.state === 'paused',
      usingWorklet: this.usingWorklet,
      tabVisible: typeof document !== 'undefined' ? document.visibilityState === 'visible' : true,
      pauseResumeCount: this.pauseResumeCount,
      heapUsedMB: perfMemory ? Math.round(perfMemory.usedJSHeapSize / 1_048_576) : null,
      audioDeviceId: track?.getSettings()?.deviceId ?? null,
      consecutiveStallChecks: this.consecutiveStallChecks,
    };
  }

  getState(): AudioManagerState {
    return this.state;
  }

  getIsPaused(): boolean {
    return this.state === 'paused';
  }

  getAllPCMData(): Int16Array {
    if (this.allPCMData.length === 0) {
      return new Int16Array(0);
    }
    const totalLength = this.allPCMData.reduce((sum, arr) => sum + arr.length, 0);
    const combined = new Int16Array(totalLength);
    let offset = 0;
    for (const chunk of this.allPCMData) {
      combined.set(chunk, offset);
      offset += chunk.length;
    }
    return combined;
  }

  clearPCMData(): void {
    this.allPCMData = [];
  }

  /**
   * Retained PCM plus the capture range it occupies, read in one synchronous
   * pass so the audio and its offsets cannot skew against each other.
   *
   * `clearPCMData()` only ever runs after a successful upload and every
   * captured frame is appended, so what is retained is exactly the capture
   * suffix `[capturedSamples - retained, capturedSamples)`.
   */
  getUploadSnapshot(): CaptureUploadSnapshot {
    const pcm = this.getAllPCMData();
    return {
      pcm,
      captureStartSample: this.capturedSamples - pcm.length,
      captureEndSample: this.capturedSamples,
      captureEpochId: this.captureEpochId,
      sampleRate: this.config.sampleRate,
    };
  }

  getSampleRate(): number {
    return this.config.sampleRate;
  }

  // ── Private: Teardown ─────────────────────────────────────────────────────

  /**
   * Disconnect and dispose the audio graph.
   *
   * @param releaseStream stop the MediaStream's tracks too (releases the mic).
   *   Recovery keeps a live stream so it can rebuild the graph without a
   *   second permission prompt.
   */
  private async teardownAudioGraph(releaseStream: boolean): Promise<void> {
    if (this.processorNode) {
      this.processorNode.disconnect();
      this.processorNode = null;
    }
    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }
    if (this.silentGain) {
      this.silentGain.disconnect();
      this.silentGain = null;
    }

    if (this.audioContext) {
      this.audioContext.removeEventListener('statechange', this.handleAudioContextStateChange);
      if (this.audioContext.state !== 'closed') {
        try {
          await this.audioContext.close();
        } catch {
          // AudioContext may already be closed
        }
      }
      this.audioContext = null;
    }

    if (releaseStream && this.mediaStream) {
      const track = this.mediaStream.getAudioTracks()[0];
      if (track) {
        track.removeEventListener('ended', this.handleTrackEnded);
      }
      for (const t of this.mediaStream.getTracks()) {
        t.stop();
      }
      this.mediaStream = null;
    }
  }

  // ── Private: Audio processing ─────────────────────────────────────────────

  /**
   * Create audio processor node.
   * Tries AudioWorklet first (modern, runs on dedicated audio thread, reliable).
   * Falls back to ScriptProcessorNode (deprecated, known to silently stall).
   */
  private async createProcessorNode(): Promise<void> {
    // Try AudioWorklet first
    if (this.audioContext?.audioWorklet && typeof AudioWorkletNode !== 'undefined') {
      try {
        const workletCode = `
          class PCMProcessorWorklet extends AudioWorkletProcessor {
            constructor() {
              super();
              this._callCount = 0;
            }
            process(inputs) {
              const ch = inputs[0] && inputs[0][0];
              if (!ch) return true;
              const len = ch.length;
              const out = new Int16Array(len);
              for (let i = 0; i < len; i++) {
                const s = Math.max(-1, Math.min(1, ch[i]));
                out[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
              }
              this.port.postMessage(out.buffer, [out.buffer]);
              if (++this._callCount % 1024 === 0) {
                this.port.postMessage({ type: 'heartbeat' });
              }
              return true;
            }
          }
          registerProcessor('pcm-processor', PCMProcessorWorklet);
        `;

        const blob = new Blob([workletCode], { type: 'application/javascript' });
        const url = URL.createObjectURL(blob);
        await this.audioContext.audioWorklet.addModule(url);
        URL.revokeObjectURL(url);

        const workletNode = new AudioWorkletNode(this.audioContext, 'pcm-processor');
        workletNode.port.onmessage = (event: MessageEvent) => {
          if (this.state !== 'recording') return;

          if (event.data?.type === 'heartbeat') {
            this.lastAudioFrameTime = Date.now();
            return;
          }

          // ArrayBuffer with Int16 PCM data from worklet — compute RMS for silence gating
          const int16 = new Int16Array(event.data as ArrayBuffer);
          if (computeInt16Rms(int16) >= SILENCE_RMS_THRESHOLD) {
            this.lastSoundTime = Date.now();
            this.hasHeardSound = true;
          }
          this.pendingBuffers.push(int16);
          if (this.config.retainAudio) this.allPCMData.push(int16);
          this.capturedSamples += int16.length;
          this.lastAudioFrameTime = Date.now();
          this.frameCount++;
          this.consecutiveStallChecks = 0;
        };

        this.processorNode = workletNode;
        this.usingWorklet = true;
        console.info('[PCMAudioManager] Using AudioWorklet processor');
        return;
      } catch (err) {
        console.warn('[PCMAudioManager] AudioWorklet failed, falling back to ScriptProcessor:', err);
        Sentry.addBreadcrumb({
          category: 'audio',
          message: 'AudioWorklet init failed, using ScriptProcessor fallback',
          level: 'warning',
        });
      }
    }

    // Fallback: ScriptProcessorNode (deprecated, known to silently stall)
    if (!this.audioContext) throw new Error('AudioContext not initialized');
    const scriptProcessor = this.audioContext.createScriptProcessor(4096, 1, 1);
    scriptProcessor.onaudioprocess = (e: AudioProcessingEvent) => {
      if (this.state !== 'recording') return;
      this.processAudioData(e.inputBuffer.getChannelData(0));
    };

    this.processorNode = scriptProcessor;
    this.usingWorklet = false;
    console.warn('[PCMAudioManager] Using ScriptProcessor (deprecated fallback)');
  }

  /**
   * Process incoming audio data (Float32 → Int16 PCM).
   * Only used by ScriptProcessor fallback.
   */
  private processAudioData(float32Data: Float32Array): void {
    this.frameCount++;
    this.lastAudioFrameTime = Date.now();
    this.consecutiveStallChecks = 0;

    // Check for silence in first frames (potential microphone issue)
    if (this.frameCount <= 5) {
      let max = 0;
      for (let i = 0; i < float32Data.length; i++) {
        max = Math.max(max, Math.abs(float32Data[i]));
      }
      if (this.frameCount === 5 && max < 0.0001) {
        console.warn('[PCMAudioManager] WARNING: First 5 frames are silent - microphone may not be capturing audio');
      }
    }

    this.ingestFrameFloat32(float32Data, Date.now());
  }

  /**
   * Convert a Float32 frame to Int16, track RMS for silence gating, and push to pendingBuffers.
   */
  private ingestFrameFloat32(float32Data: Float32Array, now: number): void {
    const rms = computeFrameRms(float32Data);
    if (rms >= SILENCE_RMS_THRESHOLD) {
      this.lastSoundTime = now;
      this.hasHeardSound = true;
    }
    const int16 = new Int16Array(float32Data.length);
    for (let i = 0; i < float32Data.length; i++) {
      const clamped = Math.max(-1, Math.min(1, float32Data[i]));
      int16[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
    }
    this.pendingBuffers.push(int16);
    if (this.config.retainAudio) this.allPCMData.push(int16);
    this.capturedSamples += int16.length;
  }

  private startTransmitTimer(): void {
    this.transmitTimer = setInterval(() => {
      this.transmitPCMData();
    }, this.config.transmitInterval);
  }

  private transmitPCMData(): void {
    if (this.pendingBuffers.length === 0) {
      return;
    }

    const totalBytes = this.pendingBuffers.reduce((n, b) => n + b.byteLength, 0);
    const joined = new Uint8Array(totalBytes);
    let offset = 0;
    for (const buf of this.pendingBuffers) {
      const bytes = new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
      joined.set(bytes, offset);
      offset += bytes.byteLength;
    }

    const joinedInt16 = new Int16Array(joined.buffer, 0, joined.byteLength >> 1);

    const now = this.__nowForTest ?? Date.now();
    const transmit = this.config.transmitSilence || shouldTransmit(now, this.lastSoundTime);

    if (!transmit) {
      // Retain as pre-roll (bounded), do not emit. No new server segments form.
      // Retain an owned copy so the pre-roll can't be aliased by buffer reuse. ~9.6KB max.
      const prerollFrame = joinedInt16.slice();
      this.prerollBuffers.push(prerollFrame);
      this.prerollSamples += prerollFrame.length;
      while (this.prerollSamples > this.prerollSamplesMax && this.prerollBuffers.length > 1) {
        const dropped = this.prerollBuffers.shift();
        if (dropped) this.prerollSamples -= dropped.length;
      }
      // Fires on every transmit tick while silent so callers get live duration updates.
      // Before the first sound, measure from recording start so a dead mic is
      // reported instead of hidden.
      const silentSince = this.lastSoundTime > 0 ? this.lastSoundTime : this.recordingStartedAt;
      if (silentSince > 0) {
        this.onSilenceStateChangeCallback?.(true, now - silentSince, this.hasHeardSound);
      }
      this.pendingBuffers = [];
      return;
    }

    // Transmitting: prepend any retained pre-roll so the word onset isn't clipped.
    let outInt16 = joinedInt16;
    if (this.prerollBuffers.length > 0) {
      const total = this.prerollSamples + joinedInt16.length;
      const merged = new Int16Array(total);
      let off = 0;
      for (const b of this.prerollBuffers) {
        merged.set(b, off);
        off += b.length;
      }
      merged.set(joinedInt16, off);
      outInt16 = merged;
      this.prerollBuffers = [];
      this.prerollSamples = 0;
    }
    this.onSilenceStateChangeCallback?.(false, 0, true);

    const pcmArrayBuffer = outInt16.buffer.slice(outInt16.byteOffset, outInt16.byteOffset + outInt16.byteLength);
    const incrementalSamples = outInt16.length;
    const incrementalDuration = incrementalSamples / this.config.sampleRate;
    this.totalSamples += incrementalSamples;
    this.totalDuration = this.totalSamples / this.config.sampleRate;

    const chunk: PCMChunk = {
      pcmData: outInt16,
      pcmArrayBuffer,
      incrementalDuration,
      totalDuration: this.totalDuration,
      totalSamples: this.totalSamples,
      sampleRate: this.config.sampleRate,
      channels: this.config.channels,
      bitDepth: this.config.bitDepth,
      // Snapshot taken synchronously with the buffer assembly above: a
      // transmitted chunk is preroll + pending, and both are always a
      // contiguous suffix of the capture stream ending at capturedSamples.
      captureEndSample: this.capturedSamples,
      captureEpochId: this.captureEpochId,
    };

    if (this.onPCMDataCallback) {
      try {
        this.onPCMDataCallback(chunk);
      } catch (err) {
        console.error('[PCMAudioManager] Error in PCM data callback:', err);
      }
    }

    this.pendingBuffers = [];
  }

  // ── Private: Health monitoring ────────────────────────────────────────────

  private startHealthCheck(): void {
    this.stopHealthCheck();
    this.healthCheckTimer = setInterval(() => {
      this.runHealthCheck();
    }, HEALTH_CHECK_INTERVAL_MS);
  }

  private stopHealthCheck(): void {
    if (this.healthCheckTimer) {
      clearInterval(this.healthCheckTimer);
      this.healthCheckTimer = null;
    }
    if (this.visibilityGraceTimer) {
      clearTimeout(this.visibilityGraceTimer);
      this.visibilityGraceTimer = null;
    }
  }

  private runHealthCheck(): void {
    // Only check in recording state
    if (this.state !== 'recording') return;

    // Skip during visibility grace period (tab was just re-foregrounded)
    if (this.healthCheckPausedForVisibility) return;

    // Skip when tab is hidden (browser throttles everything)
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

    const age = Date.now() - this.lastAudioFrameTime;

    if (age > STALL_THRESHOLD_MS) {
      this.consecutiveStallChecks++;

      if (this.consecutiveStallChecks >= CONSECUTIVE_STALLS_REQUIRED) {
        const health = this.getHealth();
        console.error(
          `[PCMAudioManager] STALL DETECTED: No audio frames for ${age}ms ` +
            `(consecutive checks: ${this.consecutiveStallChecks}, worklet: ${this.usingWorklet})`
        );

        // Report to Sentry once per stall episode
        if (!this.stallReportedToSentry) {
          this.stallReportedToSentry = true;
          Sentry.withScope((scope) => {
            scope.setFingerprint(['audio-pipeline-stall']);
            Sentry.captureMessage('audio_pipeline_stall', {
              level: 'warning',
              extra: health as unknown as Record<string, unknown>,
            });
          });
        }

        if (this.onStallCallback) {
          this.onStallCallback(health);
        }
      }
    } else {
      // Audio is healthy — reset stall tracking
      if (this.consecutiveStallChecks >= CONSECUTIVE_STALLS_REQUIRED) {
        console.info('[PCMAudioManager] Audio pipeline recovered from stall');
        Sentry.addBreadcrumb({
          category: 'audio',
          message: 'Pipeline stall recovered (frames resumed)',
          level: 'info',
        });
      }
      this.consecutiveStallChecks = 0;
      this.stallReportedToSentry = false;
    }
  }

  private startBreadcrumbs(): void {
    this.stopBreadcrumbs();
    this.breadcrumbTimer = setInterval(() => {
      if (this.state === 'recording') {
        Sentry.addBreadcrumb({
          category: 'audio',
          message: `health: frames=${this.frameCount} dur=${this.totalDuration.toFixed(1)}s ctx=${this.audioContext?.state} worklet=${this.usingWorklet}`,
          level: 'info',
        });
      }
    }, SENTRY_BREADCRUMB_INTERVAL_MS);
  }

  private stopBreadcrumbs(): void {
    if (this.breadcrumbTimer) {
      clearInterval(this.breadcrumbTimer);
      this.breadcrumbTimer = null;
    }
  }

  // ── Private: Visibility monitoring ────────────────────────────────────────

  private startVisibilityMonitoring(): void {
    if (typeof document === 'undefined') return;

    this.visibilityHandler = () => {
      if (document.visibilityState === 'hidden') {
        // Tab going to background — pause health checks to avoid false positives
        this.healthCheckPausedForVisibility = true;
      } else {
        // Tab coming back — give grace period before resuming health checks
        this.visibilityGraceTimer = setTimeout(() => {
          this.healthCheckPausedForVisibility = false;
          this.lastAudioFrameTime = Date.now(); // Reset baseline
          this.consecutiveStallChecks = 0;
        }, VISIBILITY_GRACE_PERIOD_MS);
      }
    };

    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  private stopVisibilityMonitoring(): void {
    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    if (this.visibilityGraceTimer) {
      clearTimeout(this.visibilityGraceTimer);
      this.visibilityGraceTimer = null;
    }
    this.healthCheckPausedForVisibility = false;
  }

  // ── Private: Event handlers ───────────────────────────────────────────────

  private handleAudioContextStateChange = (): void => {
    const newState = this.audioContext?.state;
    console.warn(`[PCMAudioManager] AudioContext state changed to: ${newState}`);

    Sentry.addBreadcrumb({
      category: 'audio',
      message: `AudioContext state: ${newState}`,
      level: 'warning',
    });

    // Try to auto-resume if suspended while recording
    // Note: may fail if not triggered by user gesture (autoplay policy)
    if (newState === 'suspended' && this.state === 'recording') {
      console.warn('[PCMAudioManager] AudioContext suspended unexpectedly, attempting resume...');
      this.audioContext?.resume().then(
        () => console.info('[PCMAudioManager] AudioContext resumed successfully'),
        (err) => console.error('[PCMAudioManager] Failed to resume AudioContext:', err)
      );
    }
  };

  private handleTrackEnded = (): void => {
    console.error('[PCMAudioManager] MediaStream audio track ended unexpectedly');

    const health = this.getHealth();
    Sentry.captureMessage('mediastream_track_ended', {
      level: 'error',
      extra: health as unknown as Record<string, unknown>,
    });

    // Immediately notify of stall (no need to wait for health check)
    if (this.onStallCallback && this.state === 'recording') {
      this.onStallCallback(health);
    }
  };

  // ── Test seams ───────────────────────────────────────────────────────────

  /** @internal test seam */
  __setOnPcmForTest(cb: (chunk: PCMChunk) => void): void {
    this.onPCMDataCallback = cb;
  }

  /** @internal test seam */
  __processFrameForTest(frame: Float32Array, now: number): void {
    const rms = computeFrameRms(frame);
    if (rms >= SILENCE_RMS_THRESHOLD) {
      this.lastSoundTime = now;
      this.hasHeardSound = true;
    }
    const int16 = new Int16Array(frame.length);
    for (let i = 0; i < frame.length; i++) {
      const c = Math.max(-1, Math.min(1, frame[i]));
      int16[i] = c < 0 ? c * 0x8000 : c * 0x7fff;
    }
    this.pendingBuffers.push(int16);
    if (this.config.retainAudio) this.allPCMData.push(int16);
    this.capturedSamples += int16.length;
  }

  /** @internal test seam */
  __transmitForTest(now: number): void {
    this.__nowForTest = now;
    this.transmitPCMData();
    this.__nowForTest = undefined;
  }
}
